import { useCallback } from 'react';

import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { useAppSelector } from '@/hooks/redux-hooks';
import type { UserConfig } from '@/slices/userConfigSlice';

export enum DateTextVariants {
  FULL,
  DAY_MONTH,
  MONTH_YEAR,
  WITH_TIME,
  MONTH
}

const useDateText = () => {
  const { userConfig } = useAppSelector(state => state.userConfig);

  const formatDateString = useCallback(
    (date: string, variant: DateTextVariants) => {
      const dateFormat = getDateFormat(userConfig, variant);
      const userTimezone = userConfig.timezone.value;
      try {
        const zonedDateTime = toZonedTime(
          new Date(date.includes('Z') ? date : `${date}Z`),
          userTimezone
        );
        const localDate = format(zonedDateTime, dateFormat);
        const localTime = format(zonedDateTime, 'HH:mm:ss');
        return localDate + (variant === DateTextVariants.WITH_TIME ? ` ${localTime}` : '');
      } catch {
        return date;
      }
    },
    [userConfig]
  );

  const formatDateStringInBraces = useCallback(
    (value: string, variant: DateTextVariants): string => {
      const bracePattern = /\{([^{}]+)}/g;

      return value.replaceAll(bracePattern, (_, content) => {
        return formatDateString(content.trim(), variant);
      });
    },
    [formatDateString]
  );

  return { formatDateString, formatDateStringInBraces };
};

export const getDateFormat = (userConfig: UserConfig, variant: DateTextVariants) => {
  const configFormat = userConfig.dateFormat.value;
  switch (variant) {
    case DateTextVariants.DAY_MONTH: {
      return parseDayMonth(configFormat);
    }
    case DateTextVariants.MONTH_YEAR: {
      return parseMonthYear(configFormat);
    }
    case DateTextVariants.MONTH: {
      return parseMonth(configFormat);
    }
    default:
      return configFormat;
  }
};

export function parseMonthYear(dateFormat: string): string {
  if (dateFormat.includes('MMM')) {
    return 'MMM-yy';
  }
  if (dateFormat.startsWith('yyyy')) {
    return 'yyyy-MM';
  }
  return 'MM/yyyy';
}

export function parseDayMonth(dateFormat: string): string {
  if (dateFormat.includes('MMM')) {
    return 'dd-MMM';
  }
  if (dateFormat.startsWith('yyyy')) {
    return 'MM-dd';
  }
  if (dateFormat.startsWith('MM')) {
    return 'MM/dd';
  }
  return 'dd/MM';
}

export function parseMonth(dateFormat: string): string {
  if (dateFormat.includes('MMM')) {
    return 'MMM';
  }
  return 'MM';
}

export default useDateText;
